import type { LucideIcon } from "lucide-react";
import { FileTextIcon, GlobeIcon, ImageIcon, MailIcon, PlugIcon } from "lucide-react";

import { iconForMime, resolveVaultMimeType } from "../vault/vaultShared";
import {
  describeToolCall,
  isMorrowNativeToolSlug,
  resolveIntegrationMentionInText,
} from "./workEntryStep";

const VAULT_TOOL_NAMES = new Set([
  "get_vault_item_link",
  "list_vault_items",
  "make_attachment_public",
  "read_vault_item",
  "search_vault",
]);

const FILE_EXTENSION_PATTERN = /\b[\w.-]+\.([a-z0-9]{2,5})\b/gi;

export type MorrowStepIconKind = "orb" | "vault" | "logo" | "file" | "lucide" | "wrench";

export type MorrowStepIcon = {
  readonly kind: MorrowStepIconKind;
  readonly logoUrl?: string;
  readonly FileIcon?: LucideIcon;
  readonly LucideIcon?: LucideIcon;
};

function lucideIconForBuiltInSlug(slug: string | null | undefined): LucideIcon | null {
  switch (slug) {
    case "email":
      return MailIcon;
    case "show_item":
    case "generate_image":
      return ImageIcon;
    case "generate_pdf":
      return FileTextIcon;
    case "request_app_connection":
      return PlugIcon;
    case "activate_tools":
      return null;
    default:
      return null;
  }
}

function normalizedToolName(toolName: string | undefined): string {
  return toolName?.trim().toLowerCase() ?? "";
}

function mentionsVault(text: string): boolean {
  return /\bvault\b/i.test(text);
}

/** True when the agent narrates in first person ("I'll…", "I need…", "Let me…"). */
function isAgentFirstPersonNarration(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }
  return /^(?:I(?:[''](?:m|ll|ve|d))?|Let me)\b/i.test(trimmed);
}

function fileIconFromText(text: string): LucideIcon | null {
  for (const match of text.matchAll(FILE_EXTENSION_PATTERN)) {
    const extension = match[1]?.toLowerCase();
    if (!extension) {
      continue;
    }
    const mime = resolveVaultMimeType({ name: `file.${extension}`, mimeType: "" });
    if (mime !== "application/octet-stream") {
      return iconForMime(mime);
    }
  }
  if (/\bpdf\b/i.test(text)) {
    return FileTextIcon;
  }
  return null;
}

function integrationLogoFromText(text: string): string | null {
  const integration = resolveIntegrationMentionInText(text);
  if (!integration || integration.slug === "composio_search") {
    return null;
  }
  return `https://logos.composio.dev/api/${integration.slug}`;
}

export function resolveMorrowStepIcon(input: {
  text?: string | undefined;
  toolName?: string | undefined;
  /** Reasoning notes default to the orb mascot when nothing more specific matches. */
  preferOrb?: boolean | undefined;
}): MorrowStepIcon {
  const toolName = normalizedToolName(input.toolName);
  const text = input.text?.trim() ?? "";
  const combined = `${toolName} ${text}`;

  if (toolName && VAULT_TOOL_NAMES.has(toolName)) {
    return { kind: "vault" };
  }

  if (toolName) {
    const desc = describeToolCall(input.toolName!);
    if (isMorrowNativeToolSlug(desc.slug)) {
      return { kind: "orb" };
    }
    if (desc.logoUrl) {
      return { kind: "logo", logoUrl: desc.logoUrl };
    }
    const builtInLucide = lucideIconForBuiltInSlug(desc.slug);
    if (builtInLucide) {
      return { kind: "lucide", LucideIcon: builtInLucide };
    }
    if (desc.slug === "browser") {
      return { kind: "lucide", LucideIcon: GlobeIcon };
    }
  }

  if (input.preferOrb && isAgentFirstPersonNarration(text)) {
    return { kind: "orb" };
  }

  if (mentionsVault(combined)) {
    return { kind: "vault" };
  }

  const textLogo = integrationLogoFromText(text);
  if (textLogo) {
    return { kind: "logo", logoUrl: textLogo };
  }

  const fileIcon = fileIconFromText(text);
  if (fileIcon) {
    return { kind: "file", FileIcon: fileIcon };
  }

  if (input.preferOrb || !toolName) {
    return { kind: "orb" };
  }

  return { kind: "wrench" };
}
